import { getArchetypes, isTpuLabellingEnabled } from "@/features/branding";
import { downloadCsvFile } from "@/features/custom_downloads";
import { getTranslatedText } from "@/features/internationalisation";

import { availableExposureHazards, getExposureStatusLabel, getReturnPeriodLabel } from "@/features/pe_aldd";
import { anaHazards } from "@/lib/crg_apis/constants/hazards";
import { RiskBandsAdvancedSettingsValues, TpuSubunitById } from "@/tools/risk-bands/types";
import { screeningFloodExposureReturnPeriod } from "@/tools/risk-bands/utils/shared";
import type { ExposureStatus } from "@/types/exposure";
import { concatLatLng, limitLatLngTo6dp, postProcessGeocodeQuality } from "@/utils/geocoding";
import { noDataText } from "../../../features/pe_aldd/utils/shared";
import { GeocodeWithResult } from "../components/MultipleResultsTable";

/**
 * Triggers a .csv download of a list of assets with Risk Bands data.
 */
export const downloadExposureCsv = ({
  advancedSettings,
  filename,
  results,
  tpuData,
}: {
  advancedSettings: Partial<RiskBandsAdvancedSettingsValues>;
  filename: string;
  results: GeocodeWithResult[];
  tpuData: TpuSubunitById | undefined;
}) => {
  const tpuHeaders = isTpuLabellingEnabled() && tpuData ? ["TPU", "Subunit"] : [];

  const headers = [
    "Provided Address",
    "Geocode Quality",
    "Latitude",
    "Longitude",
    ...tpuHeaders,
    "Build Year",
    "Height Above Ground",
    "Archetype",
    "Return Period",
    ...availableExposureHazards.map(
      (hazard) => `Flood Exposure_${getTranslatedText(`terms:hazards.${anaHazards[hazard].newId}`)}`
    ),
  ];

  const archetypeLabel = (archetype: string) => getArchetypes().find((a) => a.value === archetype)?.name;

  const body = results.map(({ geocode, result }) => {
    const latLng = limitLatLngTo6dp(geocode.latLng);

    const getTpu = () => {
      if (!isTpuLabellingEnabled()) return [];

      const tpuId = concatLatLng(geocode.latLng);
      return tpuData ? [tpuData?.[tpuId]?.tpu || "No Data", tpuData?.[tpuId]?.subunit || "No Data"] : [];
    };

    return [
      geocode.input,
      postProcessGeocodeQuality({ quality: geocode?.quality, latitude: latLng.lat, longitude: latLng.lng }),
      latLng.lat,
      latLng.lng,
      ...getTpu(),
      advancedSettings.buildYear?.amount,
      advancedSettings.heightAboveGround?.amount,
      archetypeLabel(advancedSettings.archetype || "") || "unknown",
      getReturnPeriodLabel(screeningFloodExposureReturnPeriod),
      ...availableExposureHazards.map((hazard) => {
        if (!("exposures" in result.rcp85) || result.rcp85.exposures === undefined || result.rcp85.exposures === null) {
          return noDataText;
        }
        return getExposureStatusLabel(
          result.rcp85.exposures[hazard][screeningFloodExposureReturnPeriod] as ExposureStatus
        );
      }),
    ];
  });

  downloadCsvFile([headers, ...body], {
    filename,
    prefixDate: true,
  });
};
